// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Torc — the field, as real DOM buttons over an SVG lattice.
//
// THE RULE THIS FILE IS BUILT AROUND: no layout geometry is ever measured in JavaScript. Every point
// is positioned by a PERCENTAGE that comes straight out of the geometry tables, and the pitch
// between neighbouring points is a CSS variable derived from the same numbers. That is what makes a
// backgrounded peer's board correct — a tab whose clientWidth is 0 has nothing to get wrong — and it
// means every tap is a button's own click handler rather than a rect hit-test computed inside a
// frame that may never have run.
//
// THE BOARD BOX IS ONE SHAPE FOR ALL THREE MODES. With the half-pitch margin below folded in, the
// box is √3·R+1 by 2R, so its aspect ratio is 1.03 / 0.99 / 0.97 across the three radii — near
// enough to square that one `aspect-ratio` box holds all of them and the modes differ only in how
// densely it is filled. The hit target is exactly one pitch across, so adjacent targets touch and
// never overlap: measured at 57.3px, 44.8px and 36.7px in a real browser at 375px.
//
// The particle canvas is the one exception and is deliberately fixed-size (never height:100%, which
// collapses to the backing-store aspect ratio inside a flex parent) with everything authored in
// 0..1 space.

import { AXES, isRing, ownerOf, isMark, type State } from './rules';
import { SEAT_NAME } from './palette';

const SVG_NS = 'http://www.w3.org/2000/svg';

export interface LastMove {
  from: number;
  to: number;
  side: number;
}

export interface ViewHints {
  /** The torc currently picked up, or the run key mid-claim. */
  picked: number | null;
  /** Points the CURRENT stage is asking for. */
  targets: Set<number>;
  /** Points that may START a turn. */
  pickable: Set<number>;
  /** Every claimable line, outlined before one is chosen. */
  offered: number[][];
  /** The line chosen for the claim in progress. */
  chosenRun: number[];
  last: LastMove | null;
  /** Studs that turned over on the last ply. */
  flipped: Set<number>;
  interactive: boolean;
  mySide: number | null;
}

export const emptyHints = (): ViewHints => ({
  picked: null,
  targets: new Set(),
  pickable: new Set(),
  offered: [],
  chosenRun: [],
  last: null,
  flipped: new Set(),
  interactive: false,
  mySide: null,
});

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  hue: string;
}

const FX = 1000;

/**
 * A hard ceiling on live particles, oldest dropped first.
 *
 * At human pace this is never reached — a ride and its flips spawn maybe forty, and they fade in
 * about a second and a half. It exists for the case where plies arrive far faster than that: a
 * catch-up burst when a peer replays a log it had fallen behind on, or a player holding down a
 * restart. Without it the field disappears under a few hundred dots and studs stop being findable,
 * which showed up immediately when a whole game was driven through in twenty seconds.
 */
const MAX_PARTICLES = 200;

export class BoardView {
  readonly root: HTMLElement;
  private pts: HTMLButtonElement[] = [];
  private fx: HTMLCanvasElement | null = null;
  private parts: Particle[] = [];
  private raf = 0;
  private dead = false;
  private readonly s0: State;

  constructor(
    host: HTMLElement,
    s: State,
    private readonly onTap: (cell: number) => void,
    private readonly reducedMotion: boolean,
  ) {
    this.s0 = s;
    const g = s.g;
    const spanX = Math.sqrt(3) * g.R;
    const spanY = 2 * g.R - 1;
    // HALF A PITCH OF MARGIN ON ALL FOUR SIDES, and it is not decoration. Every point is a tap
    // target one pitch across, centred on the point, so a lattice drawn flush to the edges of its
    // box leaves the outermost targets hanging half outside it — against the page background on a
    // phone, with a slice of each one past the edge of the screen. Adding one whole pitch to each
    // dimension puts every target inside the panel; it costs about 10% of the pitch (Hoard goes from
    // 40.5px to 37.2px at a 375px phone) and it is worth it.
    const aspect = (spanX + 1) / (spanY + 1);
    this.root = document.createElement('div');
    this.root.className = 'board';
    this.root.style.setProperty('--aspect', String(aspect));
    // One pitch as a percentage of the board's WIDTH.
    this.root.style.setProperty('--pitch', `${100 / (spanX + 1)}%`);
    /** Lattice coordinates → percentage of the board box, with that half-pitch margin folded in. */
    const atX = (i: number): number => ((0.5 + g.px[i] * spanX) / (spanX + 1)) * 100;
    const atY = (i: number): number => ((0.5 + g.py[i] * spanY) / (spanY + 1)) * 100;
    this.root.setAttribute('role', 'grid');
    this.root.setAttribute('aria-label', `${g.n} point field`);

    // The lattice is drawn in the SAME percentage space as the points, so the two can never drift.
    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('class', 'lattice');
    svg.setAttribute('viewBox', '0 0 100 100');
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');
    // preserveAspectRatio="none" would distort a stroke, so the lines are drawn with a vector-effect
    // that keeps the width in screen pixels instead.
    for (let axis = 0; axis < AXES; axis++) {
      for (let li = 0; li < g.lines.length; li++) {
        if (g.lineAxis[li] !== axis) continue;
        const line = g.lines[li];
        const a = line[0];
        const b = line[line.length - 1];
        const el = document.createElementNS(SVG_NS, 'line');
        el.setAttribute('x1', atX(a).toFixed(3));
        el.setAttribute('y1', atY(a).toFixed(3));
        el.setAttribute('x2', atX(b).toFixed(3));
        el.setAttribute('y2', atY(b).toFixed(3));
        el.setAttribute('vector-effect', 'non-scaling-stroke');
        el.setAttribute('stroke-linecap', 'round');
        svg.appendChild(el);
      }
    }
    this.root.appendChild(svg);

    for (let i = 0; i < g.n; i++) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'pt';
      b.dataset.i = String(i);
      b.style.left = `${atX(i).toFixed(3)}%`;
      b.style.top = `${atY(i).toFixed(3)}%`;
      b.innerHTML = '<i class="pc"></i><i class="dot"></i>';
      b.addEventListener('click', () => this.onTap(i));
      this.pts.push(b);
      this.root.appendChild(b);
    }
    host.appendChild(this.root);

    if (!reducedMotion) {
      // Inside the board, not beside it: particle coordinates are normalised to the BOARD's box, so
      // the canvas has to be exactly that box. It is `pointer-events: none`, so it never eats a tap.
      this.fx = document.createElement('canvas');
      this.fx.className = 'fx';
      this.fx.width = FX;
      this.fx.height = FX;
      this.fx.setAttribute('aria-hidden', 'true');
      this.root.appendChild(this.fx);
      this.tick();
    }
  }

  /** Paint the whole field. 31–85 points, once a ply — a diff would be more code than work. */
  paint(s: State, v: ViewHints): void {
    if (this.dead) return;
    const inOffered = new Set<number>();
    for (const run of v.offered) for (const c of run) inOffered.add(c);
    const chosen = new Set(v.chosenRun);

    for (let i = 0; i < this.pts.length; i++) {
      const el = this.pts[i];
      const cell = s.cell[i];
      const side = ownerOf(cell);
      el.className = 'pt';
      if (isMark(cell)) el.classList.add('stud', side === 0 ? 's0' : 's1');
      else if (isRing(cell)) el.classList.add('torc', side === 0 ? 's0' : 's1');
      if (v.picked !== null && s.phase === 'move' && v.picked === i) el.classList.add('picked');
      if (v.targets.has(i)) el.classList.add(s.phase === 'claim' ? 't-lift' : 't-ride');
      if (v.pickable.has(i) && v.interactive) el.classList.add('offer');
      if (inOffered.has(i)) el.classList.add('inrun');
      if (chosen.has(i)) el.classList.add('chosen');
      if (v.flipped.has(i)) el.classList.add('flipped');
      if (v.last && (v.last.from === i || v.last.to === i)) el.classList.add(v.last.from === i ? 'trail-from' : 'trail-to');

      const what = isMark(cell)
        ? `${SEAT_NAME[side]} stud`
        : isRing(cell)
          ? `${SEAT_NAME[side]} torc`
          : 'empty';
      const role = v.targets.has(i)
        ? s.phase === 'claim'
          ? ', can be lifted'
          : ', can ride here'
        : v.pickable.has(i) && v.interactive
          ? ', can be chosen'
          : '';
      el.setAttribute('aria-label', `${s.g.names[i]}, ${what}${role}`);
      el.disabled = !v.interactive;
    }
  }

  /** Burst at a point, in normalised board space so nothing is ever measured. */
  burst(cell: number, colour: string, n = 12): void {
    if (!this.fx || this.reducedMotion) return;
    const g = this.s0.g;
    const x = g.px[cell];
    const y = g.py[cell];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + Math.random();
      const sp = 0.004 + Math.random() * 0.011;
      this.parts.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 0.004, life: 1, hue: colour });
    }
    this.cap();
  }

  /** The ride: a streak of sparks from where the torc left to where it landed. */
  streak(from: number, to: number, colour: string): void {
    if (!this.fx || this.reducedMotion) return;
    const g = this.s0.g;
    const steps = 12;
    for (let i = 1; i <= steps; i++) {
      const t = i / steps;
      this.parts.push({
        x: g.px[from] + (g.px[to] - g.px[from]) * t,
        y: g.py[from] + (g.py[to] - g.py[from]) * t,
        vx: (g.px[to] - g.px[from]) * 0.01,
        vy: (g.py[to] - g.py[from]) * 0.01,
        life: 0.3 + t * 0.5,
        hue: colour,
      });
    }
    this.cap();
  }

  shake(px = 3): void {
    if (this.reducedMotion) return;
    this.root.style.setProperty('--shake', `${px}px`);
    this.root.classList.remove('shaking');
    void this.root.offsetWidth;
    this.root.classList.add('shaking');
  }

  nudge(cell: number): void {
    const el = this.pts[cell];
    if (!el) return;
    el.classList.remove('nope');
    void el.offsetWidth;
    el.classList.add('nope');
  }

  private cap(): void {
    if (this.parts.length > MAX_PARTICLES) this.parts.splice(0, this.parts.length - MAX_PARTICLES);
  }

  private tick = (): void => {
    if (this.dead || !this.fx) return;
    const ctx = this.fx.getContext('2d');
    if (ctx) {
      ctx.clearRect(0, 0, FX, FX);
      for (const p of this.parts) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.0004;
        p.life -= 0.026;
      }
      this.parts = this.parts.filter((p) => p.life > 0);
      for (const p of this.parts) {
        ctx.globalAlpha = Math.max(0, Math.min(1, p.life));
        ctx.fillStyle = p.hue;
        const r = 3 + p.life * 7;
        ctx.beginPath();
        ctx.arc(p.x * FX, p.y * FX, r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    this.raf = requestAnimationFrame(this.tick);
  };

  destroy(): void {
    this.dead = true;
    cancelAnimationFrame(this.raf);
    this.root.remove();
    this.fx?.remove();
  }
}

/**
 * The final field, drawn on the results screen — "here is what you were both playing on when it
 * stopped", with each side's longest unfinished line picked out. Principle #9 asks for what everyone
 * MISSED, and in this game that is the line you had four-fifths of and never closed.
 */
export function renderFinal(
  canvas: HTMLCanvasElement,
  s: State,
  colours: string[],
  palette: Record<string, string>,
  highlight: number[] = [],
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const g = s.g;
  const W = canvas.width;
  const H = canvas.height;
  // The SAME half-pitch margin the live board uses. Without it the outermost pieces are drawn with
  // their centres on the very edge and are sliced off by the canvas — which is exactly what the
  // first cut of this did, and it is only visible on the results screen.
  const spanX = Math.sqrt(3) * g.R;
  const spanY = 2 * g.R - 1;
  const pitch = Math.min(W / (spanX + 1), H / (spanY + 1));
  const ox = (W - pitch * (spanX + 1)) / 2 + pitch * 0.5;
  const oy = (H - pitch * (spanY + 1)) / 2 + pitch * 0.5;
  const X = (i: number): number => ox + g.px[i] * spanX * pitch;
  const Y = (i: number): number => oy + g.py[i] * spanY * pitch;

  ctx.clearRect(0, 0, W, H);
  ctx.fillStyle = palette.field;
  ctx.fillRect(0, 0, W, H);

  ctx.strokeStyle = palette.lattice;
  ctx.lineWidth = Math.max(1, pitch * 0.05);
  ctx.lineCap = 'round';
  for (const line of g.lines) {
    ctx.beginPath();
    ctx.moveTo(X(line[0]), Y(line[0]));
    ctx.lineTo(X(line[line.length - 1]), Y(line[line.length - 1]));
    ctx.stroke();
  }

  if (highlight.length) {
    ctx.strokeStyle = palette.last;
    ctx.lineWidth = Math.max(2, pitch * 0.16);
    ctx.globalAlpha = 0.55;
    ctx.beginPath();
    ctx.moveTo(X(highlight[0]), Y(highlight[0]));
    ctx.lineTo(X(highlight[highlight.length - 1]), Y(highlight[highlight.length - 1]));
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  for (let i = 0; i < g.n; i++) {
    const v = s.cell[i];
    const x = X(i);
    const y = Y(i);
    if (v === 0) {
      ctx.fillStyle = palette.point;
      ctx.beginPath();
      ctx.arc(x, y, pitch * 0.08, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    const side = ownerOf(v);
    ctx.fillStyle = colours[side];
    ctx.strokeStyle = colours[side];
    if (isRing(v)) {
      ctx.lineWidth = pitch * 0.17;
      ctx.beginPath();
      ctx.arc(x, y, pitch * 0.34, 0, Math.PI * 2);
      ctx.stroke();
    } else if (side === 0) {
      ctx.beginPath();
      ctx.arc(x, y, pitch * 0.29, 0, Math.PI * 2);
      ctx.fill();
    } else {
      // The Teal stud is a hexagon — silhouette is what tells the sides apart without colour.
      ctx.beginPath();
      for (let k = 0; k < 6; k++) {
        const a = (Math.PI / 3) * k - Math.PI / 2;
        const px = x + Math.cos(a) * pitch * 0.31;
        const py = y + Math.sin(a) * pitch * 0.31;
        if (k === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.closePath();
      ctx.fill();
    }
  }
}
